// Kiosk feedback sounds (DESIGN.md section 9, user feedback item 2). Match-progress sounds
// (point/serveChange/endsSwitch/gameWin/matchWin/undo) are WebAudio oscillator envelopes
// generated at runtime — no external assets. Chromium on the kiosk runs with
// autoplay-policy=no-user-gesture-required, so the very first call can play immediately with no
// prior tap.
//
// serveChange vs endsSwitch (2026-08-31): two DIFFERENT physical events on a table-tennis table,
// so they get two DELIBERATELY distinct chimes — LiveMatch.vue fires serveChange every time the
// serve passes to the other player (incl. the every-point alternation at 10-10), and endsSwitch
// only when the players physically swap ends (between games, and at 5 in the decider). Reusing
// one sound for both would make the "go switch ends" cue easy to miss inside the far more
// frequent serve-rotation chirp, so serveChange stays a quick two-tick triangle blip while
// endsSwitch is a slower four-note sine "ding-dong ×2" (doorbell interval, repeated to read as
// "both sides move") — different shape, different note count, different rhythm.
//
// scanSuccess/scanError are the ONE exception (2026-08-31, badge-scan feedback): these play the
// SAME bundled MP3s app-vue2's scanner UIs use via `ts/composables/useBeep.ts`
// (`beepOk`/`beepError`) — `public/scanner-sounds/beep-07a.mp3` / `beep-err.mp3`, copied
// byte-for-byte from `apps/app-vue2/public/scanner-sounds/` (local assets, no CDN) — so a badge
// scan sounds identical here to everywhere else in the platform, rather than inventing a new
// tone.
//
// One shared AudioContext, created lazily on first use and resumed if a browser ever suspends
// it. All failures are swallowed — sound is a nice-to-have, never allowed to break scoring.

type ToneShape = 'sine' | 'square' | 'triangle' | 'sawtooth'

interface Tone {
  freq: number
  start: number // seconds from "now"
  duration: number // seconds
  gain?: number // 0..1, relative to masterGain
  shape?: ToneShape
}

let ctx: AudioContext | null = null

function getContext(): AudioContext | null {
  if (typeof window === 'undefined') return null
  const AudioCtx = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
  if (!AudioCtx) return null
  try {
    if (!ctx) ctx = new AudioCtx()
    if (ctx.state === 'suspended') void ctx.resume().catch(() => {})
    return ctx
  } catch {
    return null
  }
}

// Tasteful office volume: short, quiet, no sustained tones.
const MASTER_GAIN = 0.14

// Mirrors app-vue2's useBeep._playFile: a plain HTMLAudioElement per call, no shared/cached
// element (overlapping scans each get their own playback instead of cutting one another off).
// Bundled asset, so no network round-trip; failures (autoplay block, decode error) are
// swallowed exactly like the WebAudio paths above.
function playFile(src: string) {
  try {
    const audio = new Audio(src)
    void audio.play().catch(() => {})
  } catch {
    // Sound is best-effort — never let a playback hiccup surface to the UI.
  }
}

function playTones(tones: Tone[]) {
  const audio = getContext()
  if (!audio) return
  try {
    const now = audio.currentTime
    for (const t of tones) {
      const osc = audio.createOscillator()
      const gain = audio.createGain()
      osc.type = t.shape ?? 'sine'
      osc.frequency.value = t.freq
      const startAt = now + t.start
      const endAt = startAt + t.duration
      const peak = (t.gain ?? 1) * MASTER_GAIN
      // Tiny linear attack/decay so every tone is a soft blip, never a click.
      gain.gain.setValueAtTime(0, startAt)
      gain.gain.linearRampToValueAtTime(peak, startAt + Math.min(0.012, t.duration / 3))
      gain.gain.linearRampToValueAtTime(0, endAt)
      osc.connect(gain).connect(audio.destination)
      osc.start(startAt)
      osc.stop(endAt + 0.02)
    }
  } catch {
    // Sound is best-effort — never let a WebAudio hiccup surface to the UI.
  }
}

export const sounds = {
  /** Generic UI tap — round-9 (2026-08-31): EVERY button press gets audio feedback. One very
   * short, quiet, low-ish sine tick — deliberately smaller/duller than every "something
   * happened" chime above it (point/serve/game/…), so taps feel tactile without competing with
   * the semantic sounds that often follow the same press. Wired globally in App.vue. */
  tap() {
    playTones([{ freq: 520, start: 0, duration: 0.035, shape: 'sine', gain: 0.5 }])
  },
  /** A point was posted (touch or ESP32) — one soft high blip. */
  point() {
    playTones([{ freq: 880, start: 0, duration: 0.09, shape: 'sine' }])
  },
  /** The serve passed to the other player — two quick ascending ticks, deliberately different
   * timbre (triangle, not sine) from the point blip so it reads as "attention", not "score".
   * Fires on every rotation, including the every-point alternation at 10-10 — kept short and
   * light on purpose since it is the most frequent chime in the set. */
  serveChange() {
    playTones([
      { freq: 660, start: 0, duration: 0.055, shape: 'triangle', gain: 0.85 },
      { freq: 740, start: 0.065, duration: 0.055, shape: 'triangle', gain: 0.85 },
    ])
  },
  /** Ends just switched (between games, or at 5 in the decider) — a slower four-note sine
   * "ding-dong ×2" (G5 → E5 twice), a classic doorbell interval repeated so it reads as "both
   * sides move", clearly distinct from serveChange's quick triangle ticks and from gameWin's
   * monotonic ascending run even though they share notes from the same scale. */
  endsSwitch() {
    playTones([
      { freq: 783.99, start: 0, duration: 0.09, shape: 'sine', gain: 0.9 },
      { freq: 659.25, start: 0.1, duration: 0.16, shape: 'sine', gain: 0.9 },
      { freq: 783.99, start: 0.32, duration: 0.09, shape: 'sine', gain: 0.9 },
      { freq: 659.25, start: 0.42, duration: 0.18, shape: 'sine', gain: 0.9 },
    ])
  },
  /** A game was won (match continues) — a short 3-note major arpeggio. */
  gameWin() {
    playTones([
      { freq: 523.25, start: 0, duration: 0.11, shape: 'sine' },
      { freq: 659.25, start: 0.1, duration: 0.11, shape: 'sine' },
      { freq: 783.99, start: 0.2, duration: 0.16, shape: 'sine' },
    ])
  },
  /** The match was won — a longer 4-note fanfare, the biggest sound in the set. */
  matchWin() {
    playTones([
      { freq: 523.25, start: 0, duration: 0.11, shape: 'sine' },
      { freq: 659.25, start: 0.11, duration: 0.11, shape: 'sine' },
      { freq: 783.99, start: 0.22, duration: 0.11, shape: 'sine' },
      { freq: 1046.5, start: 0.33, duration: 0.3, shape: 'sine' },
    ])
  },
  /** A point was undone — two short descending low tones, the mirror of "point". */
  undo() {
    playTones([
      { freq: 420, start: 0, duration: 0.08, shape: 'square', gain: 0.6 },
      { freq: 300, start: 0.09, duration: 0.11, shape: 'square', gain: 0.6 },
    ])
  },
  /** A voice command was heard AND applied (round-15) — a quick low→high two-note "wake word"
   * style chirp, triangle so it reads closer to serveChange's family (attention, not score)
   * but ascending a fifth so it's clearly its own cue. */
  voice() {
    playTones([
      { freq: 587.33, start: 0, duration: 0.07, shape: 'triangle', gain: 0.9 },
      { freq: 880, start: 0.08, duration: 0.12, shape: 'triangle', gain: 0.9 },
    ])
  },
  /** A voice command was heard but NOT applied (illegal/ambiguous score, unknown speaker) —
   * a flat double low-buzz, deliberately dull. */
  voiceReject() {
    playTones([
      { freq: 220, start: 0, duration: 0.09, shape: 'square', gain: 0.5 },
      { freq: 220, start: 0.12, duration: 0.09, shape: 'square', gain: 0.5 },
    ])
  },
  /** A badge scan resolved to a player — same asset as app-vue2's useBeep().beepOk(). */
  scanSuccess() {
    playFile('/scanner-sounds/beep-07a.mp3')
  },
  /** A badge scan failed to resolve — same asset as app-vue2's useBeep().beepError(). */
  scanError() {
    playFile('/scanner-sounds/beep-err.mp3')
  },
}
